import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { FadeUp, SectionLabel } from "./Reveal";
import { useLang } from "../lib/lang";
import { lockScroll } from "../lib/smooth";

const SETS = {
  digital: "d02 d03 d05 d06 d09 d12 d13 d14 d15 d18 d19 d21 d23".split(" "),
  analog: "a02 a04 a06 a08 a10 a13 a14 a15 a17 a18 a19 a20".split(" "),
};
const src = (n) => `/work/photo/${n}.jpg`;

const Lightbox = ({ list, index, setIndex }) => {
  const open = index !== null;
  useEffect(() => {
    lockScroll(open);
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setIndex(null);
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % list.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + list.length) % list.length);
    };
    window.addEventListener("keydown", onKey);
    return () => { window.removeEventListener("keydown", onKey); lockScroll(false); };
  }, [open, list.length, setIndex]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div data-testid="photo-lightbox" data-lenis-prevent className="fixed inset-0 z-[85] grid place-items-center bg-black/95 p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIndex(null)}>
          <motion.img key={list[index]} src={src(list[index])} alt="" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="max-h-[86vh] max-w-full rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
          <button data-testid="photo-lightbox-close" onClick={() => setIndex(null)} className="absolute right-4 top-4 grid h-12 w-12 place-items-center rounded-full bg-[var(--acid)] text-[var(--ink)]" aria-label="close"><X size={20} /></button>
          <button data-testid="photo-lightbox-prev" onClick={(e) => { e.stopPropagation(); setIndex((i) => (i - 1 + list.length) % list.length); }} className="absolute left-3 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20" aria-label="prev"><ChevronLeft /></button>
          <button data-testid="photo-lightbox-next" onClick={(e) => { e.stopPropagation(); setIndex((i) => (i + 1) % list.length); }} className="absolute right-3 top-1/2 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white hover:bg-white/20" aria-label="next"><ChevronRight /></button>
          <span className="absolute bottom-5 font-mono text-xs text-zinc-300">{index + 1} / {list.length}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const Photography = () => {
  const { tr } = useLang();
  const [set, setSet] = useState("digital");
  const [index, setIndex] = useState(null);
  const list = SETS[set];
  const tabs = [
    { id: "digital", label: { es: "Digital", en: "Digital" } },
    { id: "analog", label: { es: "Analógica · 35mm", en: "Analog · 35mm" } },
  ];

  return (
    <section id="photo" data-testid="photography-section" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-36">
      <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
        <div>
          <SectionLabel index="04">{tr({ es: "Fotografía", en: "Photography" })}</SectionLabel>
          <FadeUp as="h2" className="mt-6 font-display text-5xl font-semibold uppercase leading-[0.95] tracking-tight sm:text-7xl">
            {tr({ es: "Mirar", en: "Looking" })} <span className="font-serif-i normal-case text-[var(--acid)]">{tr({ es: "primero.", en: "first." })}</span>
          </FadeUp>
          <FadeUp delay={0.1} className="mt-5 max-w-md text-zinc-300">
            {tr({ es: "Viajes, ciudades y luz. La fotografía es mi principal fuente de inspiración: la creatividad nace de observar.", en: "Travel, cities and light. Photography is my main source of inspiration: creativity is born from observation." })}
          </FadeUp>
        </div>
        <div className="flex rounded-full border border-white/15 p-1">
          {tabs.map((t) => (
            <button key={t.id} data-testid={`photo-tab-${t.id}`} onClick={() => setSet(t.id)} className={`relative rounded-full px-5 py-2 text-sm transition-colors ${set === t.id ? "text-[var(--ink)]" : "text-zinc-300 hover:text-white"}`}>
              {set === t.id && <motion.span layoutId="photo-pill" className="absolute inset-0 rounded-full bg-[var(--acid)]" />}
              <span className="relative">{tr(t.label)}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="mt-12 columns-2 gap-3 sm:gap-4 md:columns-3 lg:columns-4">
        {list.map((n, i) => (
          <motion.button
            key={n}
            data-testid={`photo-${n}`}
            onClick={() => setIndex(i)}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: (i % 8) * 0.05 }}
            className="group mb-3 block w-full overflow-hidden rounded-xl sm:mb-4"
          >
            <img src={src(n)} alt={tr({ es: "Fotografía", en: "Photograph" })} loading="lazy" className="w-full transition-transform duration-700 group-hover:scale-105" />
          </motion.button>
        ))}
      </div>
      <Lightbox list={list} index={index} setIndex={setIndex} />
    </section>
  );
};
